import * as React from 'react';

import {Routing} from 'nutshell-core/routing';
import * as Schema from 'nutshell-core/schema';
import type {CoreEntityType, FilterObject, EntityListViewType} from 'nutshell-core/types';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import {getEntityGraduatedFilters} from '../../../entities/entities-constants';
import {appHistory} from '../../../history';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {AudienceMemberStatusFilter} from '../../email-marketing/audiences/audience-list/audience-member-status-filter';
import {updateUrlParam} from '../../reports/update-url-param';

import {adjustStagesetSchemaField} from './adjust-stageset-schema-field';
import {EntityListEntityCount} from './entity-list-entity-count';
import {EntityListFilterButton} from './entity-list-filter-button';
import {EntityListGraduatedFilters} from './entity-list-graduated-filters';
import {EntityListSearchBox} from './entity-list-search-box';
import {EntityListViewSelector} from './entity-list-view-selector';

import styles from './entity-list-controls.module.css';

type Props = {
    currentView: EntityListViewType;
    entityType: CoreEntityType;
    entityCount: number; // The count of filtered rows,
    onViewChange: (arg1: string) => any;
    schema: Schema.Properties;
    activeFilters: FilterObject[];
    visibleActiveFilters: FilterObject[];
    isRefreshingFilters: boolean;
    addFilters: (arg1: FilterObject) => any;
    removeFilters: (arg1: FilterObject[] | FilterObject) => any;
    removeAllFilters: () => any;
    updateFilter: (arg1: FilterObject, arg2: FilterObject) => any;
    currentSearch: string | null | undefined;
    updateSearch: (arg1: string) => any;
    isViewingAudience: boolean;
    hideViewSelector?: boolean;
    hideFilterButton?: boolean;
};

const GraduatedFilters = adjustStagesetSchemaField(EntityListGraduatedFilters);

export function EntityListControls(props: Props) {
    const {user} = useGetSessionUser();
    const handleClearClick = (e: React.SyntheticEvent) => {
        if (e) {
            e.preventDefault();
        }

        if (props.isViewingAudience) {
            props.removeFilters(props.visibleActiveFilters);
        } else {
            props.removeAllFilters();
        }
    };

    // Only the list view is visible if the user doesn't have access to the CRM
    React.useEffect(() => {
        if (user && !user.permissions.canAccessCrm) {
            props.onViewChange('list');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // We add a default filter for the map and board views
    // thus, for those views, one is effectively none
    const minFilter = props.currentView === 'map' || props.currentView === 'board' ? 1 : 0;

    // We want to consider the page `filtered`
    // if there are any filters *or* a search query
    const canClearFilters =
        props.visibleActiveFilters.length > minFilter || Boolean(props.currentSearch);

    const graduatedFilters = getEntityGraduatedFilters(props.entityType);
    const nonGradulatedActiveFilters = props.visibleActiveFilters.filter(
        // @ts-expect-error
        (filter) => !graduatedFilters.includes(Object.keys(filter)[0])
    );

    const urlParams = Routing.deparam(window.location.search);
    const audienceId = urlParams.audience;

    const paramChanger = updateUrlParam(window.location.pathname, urlParams, appHistory.replace);

    return (
        <div className={styles.container}>
            <div className={styles['top-row']}>
                <div className={styles['top-row-left-group']}>
                    {!props.hideFilterButton && (
                        <EntityListFilterButton activeFilters={nonGradulatedActiveFilters} />
                    )}
                    {props.entityType === 'contacts' && audienceId ? (
                        <AudienceMemberStatusFilter
                            audienceId={audienceId}
                            // Note, this is the prop, not the filtered filters.
                            // We want to reflect the "default" filter here
                            filters={props.activeFilters}
                            onParamChange={paramChanger}
                            urlParams={urlParams}
                        />
                    ) : (
                        <GraduatedFilters
                            currentView={props.currentView}
                            entityType={props.entityType}
                            schema={props.schema}
                            activeFilters={props.activeFilters}
                            addFilters={props.addFilters}
                            removeFilters={props.removeFilters}
                            updateFilter={props.updateFilter}
                        />
                    )}
                    <StackLegacy spacing={8} alignCenter={true} isHorizontal={true}>
                        <EntityListSearchBox
                            entityType={props.entityType}
                            value={props.currentSearch}
                            onChange={props.updateSearch}
                        />
                        <EntityListEntityCount
                            entityType={props.entityType}
                            entityCount={props.entityCount}
                            activeFilters={props.activeFilters}
                            isRefreshingFilters={props.isRefreshingFilters}
                            currentSearch={props.currentSearch}
                        />
                    </StackLegacy>
                    {canClearFilters ? (
                        <a
                            role='button'
                            onClick={handleClearClick}
                            className={styles['remove-filters']}
                        >
                            Remove filters
                        </a>
                    ) : null}
                </div>
                {user && user.permissions.canAccessCrm && !props.hideViewSelector ? (
                    <div className={styles['top-row-right-group']}>
                        <EntityListViewSelector
                            currentView={props.currentView}
                            entityType={props.entityType}
                            onChange={props.onViewChange}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
